/**
 * ч.945: швидке створення ДОГОВОРУ — спільний компонент (імпорт виписки + каса).
 * Раніше жив усередині BankImportPage; винесено, щоб «створити» біля поля
 * «Договір» працювало однаково скрізь (рішення власника).
 */
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api } from '@/api/client';
import { refs } from '@/api/refs';
import { contractAccountRules } from '@/api/contractAccountRules';
import { type VatMethod } from '@/api/catalogs';
import AccountSelect from '@/components/AccountSelect';
import DatePopover from '@/components/DatePopover';
import { CONTRACT_TYPE_LABELS } from '@/shared/utils/contractTypes';
import { invalidateRefLists } from '@/shared/utils/refLists';

export default function QuickContractModal({ orgId, contractorId, contractorName, direction, operationType, onClose, onCreated }: {
  orgId: string; contractorId: string; contractorName: string; direction: 'IN' | 'OUT'; operationType?: string;
  onClose: () => void; onCreated: (id: string) => void;
}) {
  // ч.932 (питання власника): тип договору обирається тут; за замовчуванням —
  // за напрямом, для позики/фіндопомоги — «інший»; для транзиту (комісія/
  // доручення) — «з комітентом» (ч.942). На проводки тип НЕ впливає.
  const qc = useQueryClient();
  const isLoan = ['LOAN', 'LOAN_RETURN', 'LOAN_GIVEN', 'LOAN_GIVEN_RETURN'].includes(operationType ?? '');
  const isTransit = ['TRANSIT_IN', 'TRANSIT_OUT'].includes(operationType ?? '');
  const [ctype, setCtype] = useState<string>(
    isTransit ? 'WITH_COMMITTENT' : isLoan ? 'OTHER' : direction === 'IN' ? 'WITH_BUYER' : 'WITH_SUPPLIER');
  const [name, setName] = useState(
    isTransit ? 'Договір комісії' : isLoan ? 'Договір позики'
      : direction === 'IN' ? 'Договір поставки' : 'Договір закупівлі');
  const [number, setNumber] = useState('');
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [err, setErr] = useState<string | null>(null);
  // ч.942: розширені умови — поля картки договору (згорнуті за замовчуванням).
  const [showMore, setShowMore] = useState(false);
  const [priceTypeId, setPriceTypeId] = useState('');
  const [discountPercent, setDiscountPercent] = useState('');
  const [vatMethod, setVatMethod] = useState<VatMethod>('FIRST_EVENT');
  const [vatRateId, setVatRateId] = useState('');
  const [settlementForm, setSettlementForm] = useState('CASHLESS');
  const [settlementType, setSettlementType] = useState('BY_CONTRACT');
  // ч.943 (уточнення власника): у попапі задаються САМІ РАХУНКИ ОБЛІКУ
  // (стаття витрат тут не потрібна). Порожньо — типові/правила, як раніше.
  const [accReceivable, setAccReceivable] = useState('');
  const [accPayable, setAccPayable] = useState('');
  const [accAdvReceived, setAccAdvReceived] = useState('');
  const [accAdvIssued, setAccAdvIssued] = useState('');

  const currenciesQ = useQuery({ queryKey: ['currencies'], queryFn: refs.currencies });
  const uah = currenciesQ.data?.find((c) => c.code === 'UAH');
  const priceTypesQ = useQuery({
    queryKey: ['price-types'], queryFn: () => refs.priceTypes.list(),
    staleTime: 3600_000, enabled: showMore,
  });
  const vatRatesQ = useQuery({
    queryKey: ['vat-rates'], queryFn: () => refs.vatRates(), staleTime: 3600_000, enabled: showMore,
  });

  const create = useMutation({
    mutationFn: async () => {
      const c = await api.post<{ id: string }>('/refs/contracts', {
        contractor_id: contractorId, organization_id: orgId,
        name: name.trim(), number: number.trim() || null, contract_date: date || null,
        currency_id: uah?.id, contract_type: ctype,
        // ч.942: розширені умови (порожні — бекенд візьме свої дефолти)
        price_type_id: priceTypeId || null,
        discount_percent: discountPercent || '0',
        vat_method: vatMethod,
        vat_rate_id: vatRateId || null,
        settlement_form: settlementForm,
        settlement_type: settlementType,
      }).then((r) => r.data);
      // ч.943: рахунки обліку — персональне правило договору (той самий
      // endpoint, що й панель «Рахунки обліку» у картці договору).
      if (accReceivable || accPayable || accAdvReceived || accAdvIssued) {
        await contractAccountRules.putCard({
          organization_id: orgId, contract_id: c.id,
          receivable_account_id: accReceivable || null,
          payable_account_id: accPayable || null,
          advance_received_account_id: accAdvReceived || null,
          advance_issued_account_id: accAdvIssued || null,
        });
      }
      return c;
    },
    // ч.1267: створений договір одразу є в усіх списках договорів
    onSuccess: (c) => { invalidateRefLists(qc, 'contracts'); onCreated(c.id); },
    onError: (e: unknown) => {
      const d = (e as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail;
      setErr(typeof d === 'string' ? d : Array.isArray(d) ? d.map((x: { msg?: string }) => x.msg).join('; ') : 'Не вдалося створити договір');
    },
  });
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'var(--overlay)', zIndex: 1200,
      display: 'flex', alignItems: 'center', justifyContent: 'center' }} onClick={onClose}>
      <div className="card" style={{ width: showMore ? 720 : 520, maxWidth: '95vw', padding: '1rem',
        maxHeight: '90vh', overflowY: 'auto' }} onClick={(e) => e.stopPropagation()}>
        <h3 style={{ margin: '0 0 0.6rem' }}>Новий договір</h3>
        <div style={{ fontSize: '0.85rem', color: 'var(--muted)', marginBottom: '0.5rem' }}>{contractorName}</div>
        <div className="field"><label>Назва *</label>
          <input value={name} onChange={(e) => setName(e.target.value)} autoFocus /></div>
        {/* ч.942: тип + номер + дата — в ОДИН рядок. */}
        <div className="doc-grid">
          <div className="field" style={{ gridColumn: 'span 6' }}><label>Тип договору</label>
            <select value={ctype} onChange={(e) => setCtype(e.target.value)}>
              {Object.entries(CONTRACT_TYPE_LABELS).map(([t, l]) => (
                <option key={t} value={t}>{l}</option>
              ))}
            </select></div>
          <div className="field" style={{ gridColumn: 'span 3' }}><label>Номер</label>
            <input value={number} onChange={(e) => setNumber(e.target.value)} /></div>
          <div className="field" style={{ gridColumn: 'span 3' }}><label>Дата</label>
            <DatePopover value={date} onChange={(v) => setDate(v)} /></div>
        </div>
        <button type="button" className="btn-doc" style={{ marginBottom: '0.5rem' }}
          onClick={() => setShowMore((v) => !v)}>
          {showMore ? 'згорнути розширені умови' : 'розширені умови'}
        </button>
        {showMore && (
          <div style={{ border: '1px dashed var(--border)', borderRadius: 6, padding: '0.6rem',
            marginBottom: '0.5rem' }}>
            <div style={{ fontSize: '0.8rem', color: 'var(--muted)', marginBottom: '0.4rem' }}>
              Умови роботи
            </div>
            <div className="doc-grid">
              <div className="field" style={{ margin: 0 }}><label>Тип цін</label>
                <select value={priceTypeId} onChange={(e) => setPriceTypeId(e.target.value)}>
                  <option value="">— не вказано —</option>
                  {(priceTypesQ.data ?? []).map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
                </select></div>
              <div className="field" style={{ margin: 0 }}><label>Знижка, %</label>
                <input type="number" min="0" max="100" step="0.01" value={discountPercent}
                  placeholder="0" onChange={(e) => setDiscountPercent(e.target.value)} /></div>
              <div className="field" style={{ margin: 0 }}
                title="Розріз обліку боргу: по договору в цілому або по кожній накладній (як в 1С).">
                <label>Ведення взаєморозрахунків</label>
                <select value={settlementType} onChange={(e) => setSettlementType(e.target.value)}>
                  <option value="BY_CONTRACT">по договору в цілому</option>
                  <option value="BY_DOCUMENT">по розрахункових документах</option>
                  <option value="BY_BILL">по рахунках</option>
                </select></div>
            </div>
            <div style={{ fontSize: '0.8rem', color: 'var(--muted)', margin: '0.6rem 0 0.4rem' }}>
              Рахунки обліку
            </div>
            <div className="doc-grid">
              <div className="field" style={{ gridColumn: 'span 6', margin: 0 }}><label>Розрахунки з покупцем</label>
                <AccountSelect value={accReceivable} onChange={setAccReceivable}
                  placeholder="361 (типово)" /></div>
              <div className="field" style={{ gridColumn: 'span 6', margin: 0 }}><label>Розрахунки з постачальником</label>
                <AccountSelect value={accPayable} onChange={setAccPayable}
                  placeholder="631 (типово)" /></div>
              <div className="field" style={{ gridColumn: 'span 6', margin: 0 }}><label>Аванси одержані</label>
                <AccountSelect value={accAdvReceived} onChange={setAccAdvReceived}
                  placeholder="6811 (типово)" /></div>
              <div className="field" style={{ gridColumn: 'span 6', margin: 0 }}><label>Аванси видані</label>
                <AccountSelect value={accAdvIssued} onChange={setAccAdvIssued}
                  placeholder="371 (типово)" /></div>
            </div>
            <div style={{ fontSize: '0.75rem', color: 'var(--muted)', marginTop: '0.3rem' }}>
              Порожньо — діють типові рахунки (нерезидент 362/632 система визначає сама).
              Заповнене поле створює персональне правило договору.
            </div>
            <div style={{ fontSize: '0.8rem', color: 'var(--muted)', margin: '0.6rem 0 0.4rem' }}>
              Податковий облік
            </div>
            <div className="doc-grid">
              <div className="field" style={{ gridColumn: 'span 4', margin: 0 }}><label>Метод ПДВ</label>
                <select value={vatMethod} onChange={(e) => setVatMethod(e.target.value as VatMethod)}>
                  <option value="FIRST_EVENT">За першою подією</option>
                  <option value="ON_PAYMENT">За касовим методом (за оплатою)</option>
                </select></div>
              <div className="field" style={{ gridColumn: 'span 4', margin: 0 }}><label>Ставка ПДВ за замовч.</label>
                <select value={vatRateId} onChange={(e) => setVatRateId(e.target.value)}>
                  <option value="">— з документа —</option>
                  {(vatRatesQ.data ?? []).map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
                </select></div>
              <div className="field" style={{ gridColumn: 'span 4', margin: 0 }}><label>Форма розрахунків (для ПН)</label>
                <select value={settlementForm} onChange={(e) => setSettlementForm(e.target.value)}>
                  <option value="CASHLESS">Безготівкова</option>
                  <option value="CASH">Готівкова</option>
                  <option value="BARTER">Бартер</option>
                  <option value="OTHER">Інша</option>
                </select></div>
            </div>
          </div>
        )}
        <div style={{ fontSize: '0.8rem', color: 'var(--muted)', marginBottom: '0.5rem' }}>
          Решта реквізитів (валюта, вид за ЦК, умови оплати) — у картці договору. Рахунок обліку
          (631/361/6851…) визначає вид операції рядка, а не тип договору.
        </div>
        {err && <div className="error">{err}</div>}
        <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
          <button type="button" onClick={onClose}>скасувати</button>
          <button type="button" className="primary" disabled={create.isPending || !uah || name.trim().length < 2}
            onClick={() => { setErr(null); create.mutate(); }}>створити</button>
        </div>
      </div>
    </div>
  );
}

